/**
 * Skeleton primitives — placeholder blocks rendered while async data loads.
 * Match the layout of the real component so the page reflows instead of jumping.
 *
 * Tokens (`--ink-faint`, `--accent-bg`) and the `nim-skeleton-shimmer`
 * keyframes live in `web/src/index.css` so they ship through Vite's CSS
 * pipeline (the animation needs to resolve at first paint, not after a
 * runtime style injection).
 */
import React from 'react';

const baseBg = 'var(--ink-faint)';
const shineBg = 'var(--accent-bg)';

export interface SkeletonProps {
  width?: number | string;
  height?: number | string;
  rounded?: number | string;
  className?: string;
  style?: React.CSSProperties;
}

export function Skeleton({ width, height = 12, rounded = 4, className, style }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={className}
      style={{
        display: 'inline-block',
        width,
        height,
        borderRadius: rounded,
        background: `linear-gradient(90deg, ${baseBg} 0%, ${shineBg} 50%, ${baseBg} 100%)`,
        backgroundSize: '200% 100%',
        animation: 'nim-skeleton-shimmer 1.4s ease-in-out infinite',
        ...style,
      }}
    />
  );
}

/** A block of stacked skeleton lines — useful for titles + supporting copy. */
export function SkeletonLines({ lines = 3, lastWidth = '70%', gap = 8 }: { lines?: number; lastWidth?: string | number; gap?: number }) {
  return (
    <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          width={i === lines - 1 ? lastWidth : '100%'}
          height={i === 0 ? 16 : 12}
        />
      ))}
    </div>
  );
}

/** Square / circle skeleton — for avatars, icons, badges. */
export function SkeletonCircle({ size = 24 }: { size?: number }) {
  return <Skeleton width={size} height={size} rounded="50%" />;
}

/** Notion-style metric tile: top label, big number, sub copy. */
export function SkeletonStatTile() {
  return (
    <div
      aria-hidden="true"
      className="border-b-2 pb-4"
      style={{ borderColor: 'var(--border)' }}
    >
      <Skeleton width={92} height={10} rounded={3} />
      <div style={{ marginTop: 10 }}>
        <Skeleton width={84} height={26} rounded={5} />
      </div>
      <div style={{ marginTop: 8 }}>
        <Skeleton width={120} height={9} rounded={3} />
      </div>
    </div>
  );
}

/** Generic table skeleton: header row + N body rows. Pass `cols` widths as
 *  percentages to keep the column rhythm of the real table. */
export function SkeletonTable({
  rows = 6,
  cols = ['40%', '20%', '15%', '15%', '10%'],
  rowHeight = 44,
}: {
  rows?: number;
  cols?: string[];
  rowHeight?: number;
}) {
  const headCols = cols.length;
  return (
    <div
      aria-hidden="true"
      style={{
        overflow: 'hidden',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        background: 'var(--bg)',
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse', font: '14px var(--font-sans)' }}>
        <thead>
          <tr
            style={{
              borderBottom: '1px solid var(--border)',
              background: 'var(--accent-bg)',
              fontSize: 11,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: 'var(--ink-secondary)',
            }}
          >
            {Array.from({ length: headCols }).map((_, i) => (
              <th key={i} style={{ padding: '10px 12px', textAlign: 'left', width: cols[i] }}>
                <Skeleton width={60} height={8} rounded={3} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r} style={{ borderBottom: '1px solid var(--border)' }}>
              {cols.map((w, c) => (
                <td key={c} style={{ padding: '12px', width: w }}>
                  <Skeleton
                    width={c === 0 ? `${50 + (r % 3) * 18}%` : c === cols.length - 1 ? '60%' : '70%'}
                    height={12}
                  />
                </td>
              ))}
            </tr>
          ))}
          <tr aria-hidden style={{ height: rowHeight }} />
        </tbody>
      </table>
    </div>
  );
}

/** Kanban-style column skeleton: a header dot + label + count, then N card slots. */
export function SkeletonKanbanColumn({ cards = 3 }: { cards?: number }) {
  return (
    <div aria-hidden="true" className="flex-1 min-w-[120px]">
      <div className="flex items-center gap-2 mb-2 px-1">
        <SkeletonCircle size={8} />
        <Skeleton width={70} height={9} rounded={3} />
        <span className="ml-auto">
          <Skeleton width={14} height={9} rounded={3} />
        </span>
      </div>
      <div
        className="rounded-lg p-2 space-y-2"
        style={{ background: 'var(--ink-faint)', minHeight: 120 }}
      >
        {Array.from({ length: cards }).map((_, i) => (
          <div
            key={i}
            className="rounded-md p-2.5"
            style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}
          >
            <Skeleton width="100%" height={10} />
            <div style={{ marginTop: 8 }}>
              <Skeleton width="78%" height={10} />
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
              <Skeleton width={64} height={12} rounded={10} />
              <Skeleton width={32} height={12} rounded={10} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Card-shaped row skeleton used by Activity/Recent lists (icon + 2 lines + meta). */
export function SkeletonListRow() {
  return (
    <div
      aria-hidden="true"
      className="p-4 flex items-center justify-between gap-4"
      style={{ borderBottom: '1px solid var(--border)' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
        <Skeleton width={64} height={10} rounded={3} />
        <Skeleton width={120} height={10} rounded={3} />
        <Skeleton width={70} height={14} rounded={10} />
      </div>
      <Skeleton width={56} height={10} rounded={3} />
    </div>
  );
}

/** Card with a header (icon + title) + N list rows. Used by AgentRunners, Issues. */
export function SkeletonListCard({
  rows = 4,
  titleWidth = 110,
}: {
  rows?: number;
  titleWidth?: number;
}) {
  return (
    <div
      aria-hidden="true"
      style={{
        background: 'var(--bg)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '14px 20px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <SkeletonCircle size={14} />
        <Skeleton width={titleWidth} height={11} rounded={3} />
        <span style={{ marginLeft: 8 }}>
          <Skeleton width={140} height={9} rounded={3} />
        </span>
      </div>
      <div>
        {Array.from({ length: rows }).map((_, i) => (
          <SkeletonListRow key={i} />
        ))}
      </div>
    </div>
  );
}

/** Rule-shaped card: row of 3 input placeholders + textarea placeholder. */
export function SkeletonRuleCard() {
  return (
    <div
      aria-hidden="true"
      style={{
        borderRadius: 8,
        border: '1px solid var(--border)',
        background: 'var(--bg)',
        padding: 16,
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 12 }}>
        <div style={{ gridColumn: 'span 5' }}>
          <Skeleton width={40} height={8} rounded={3} />
          <div style={{ marginTop: 6 }}>
            <Skeleton width="100%" height={28} rounded={6} />
          </div>
        </div>
        <div style={{ gridColumn: 'span 3' }}>
          <Skeleton width={56} height={8} rounded={3} />
          <div style={{ marginTop: 6 }}>
            <Skeleton width="100%" height={28} rounded={6} />
          </div>
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <Skeleton width={48} height={8} rounded={3} />
          <div style={{ marginTop: 6 }}>
            <Skeleton width="100%" height={28} rounded={6} />
          </div>
        </div>
        <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'flex-end', gap: 8 }}>
          <Skeleton width={80} height={28} rounded={6} />
          <Skeleton width={28} height={28} rounded={6} />
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <Skeleton width={70} height={8} rounded={3} />
        <div style={{ marginTop: 6 }}>
          <Skeleton width="100%" height={26} rounded={6} />
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <Skeleton width={140} height={8} rounded={3} />
        <div style={{ marginTop: 6 }}>
          <Skeleton width="100%" height={64} rounded={6} />
        </div>
      </div>
    </div>
  );
}

/** Card matching the repo header: avatar + 2 title lines + meta row + metrics column. */
export function SkeletonRepoHeader() {
  return (
    <div
      aria-hidden="true"
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        background: 'var(--bg)',
        padding: 16,
        display: 'flex',
        gap: 16,
        alignItems: 'flex-start',
        flexWrap: 'wrap',
      }}
    >
      <Skeleton width={40} height={40} rounded={8} />
      <div style={{ flex: 1, minWidth: 240 }}>
        <Skeleton width={220} height={18} rounded={5} />
        <div style={{ marginTop: 8 }}>
          <Skeleton width="80%" height={11} rounded={3} />
        </div>
        <div style={{ marginTop: 10, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} width={60 + i * 10} height={11} rounded={3} />
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 16, textAlign: 'right' }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i}>
            <Skeleton width={60} height={8} rounded={3} />
            <div style={{ marginTop: 8 }}>
              <Skeleton width={50} height={18} rounded={5} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Chart placeholder: line chart, bar list, etc. */
export function SkeletonChart({ height = 120 }: { height?: number }) {
  return (
    <div
      aria-hidden="true"
      style={{
        height,
        borderRadius: 6,
        background: 'linear-gradient(90deg, var(--ink-faint) 0%, var(--accent-bg) 50%, var(--ink-faint) 100%)',
        backgroundSize: '200% 100%',
        animation: 'nim-skeleton-shimmer 1.4s ease-in-out infinite',
      }}
    />
  );
}

/** Bar-list placeholder row: label + bar + count. */
export function SkeletonBarRow() {
  return (
    <div aria-hidden="true" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <Skeleton width={112} height={10} rounded={3} />
      <span style={{ flex: 1 }}>
        <Skeleton width="100%" height={8} rounded={4} />
      </span>
      <Skeleton width={20} height={10} rounded={3} />
    </div>
  );
}

/** Grid of N stat tiles matching Reliability/AgentRunners/Stats layouts. */
export function SkeletonStatGrid({ count = 4 }: { count?: number }) {
  return (
    <div
      aria-hidden="true"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`,
        gap: 16,
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonStatTile key={i} />
      ))}
    </div>
  );
}

/** Bento card (SBOM-style stat block). */
export function SkeletonBentoTile() {
  return (
    <div
      aria-hidden="true"
      style={{
        background: 'var(--bg)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 16,
        height: 112,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Skeleton width={130} height={9} rounded={3} />
      <div>
        <Skeleton width={70} height={22} rounded={5} />
        <div style={{ marginTop: 6 }}>
          <Skeleton width={110} height={9} rounded={3} />
        </div>
      </div>
    </div>
  );
}